import { useState, useEffect } from "react";
import axios from "axios";
import ThemeMaker from "../../components/thememaker";
import { config } from "../../utils/config";
import { tokenDecode } from "../../utils/helper";
import { toast } from "react-toastify";
import { Paginated } from "../../components/Paginated";
import "../../components/table.css";
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import brandStyles from "../Brand/Brand.module.css";
import warehouseStyles from "./WarehouseList.module.css";
import "../../components/mcrbTableTheme.css";

const { apiEndpoint } = config;

const Sstyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: "16px",
    bgcolor: 'background.paper',
    boxShadow: '0 20px 50px rgba(43, 22, 87, 0.24)',
    outline: 'none',
    border: 'none',
    p: 0,
};

const WarehouseList = () => {
    const decoded: any = tokenDecode();

    const [warehouseList, setWarehouseList] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sellerList, setSellerList] = useState<any[]>([]);
    const [sellerFilterId, setSellerFilterId] = useState("");

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewData, setViewData] = useState<any>(null);

    const fetchWarehouses = async (sellerId?: string) => {
        setIsLoading(true);
        try {
            const { data } = await axios({
                url: `${apiEndpoint}/admin/warehouses`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`,
                },
                // Laravel-style paginator; ask for a high per_page so the existing
                // client-side Paginated table (used everywhere else in the app)
                // can show/paginate the full result set in one go.
                params: { ...(sellerId ? { seller_id: sellerId } : {}), per_page: 200 },
            });

            const list = data?.data?.data || [];
            setWarehouseList(list);
        } catch (error) {
            console.log("error", error);
            toast.error("Error loading warehouses");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSellerList = async () => {
        try {
            const { data } = await axios({
                url: `${apiEndpoint}/getsellerlist`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`,
                },
            });
            if (data.status === 200) {
                setSellerList(data.data);
            }
        } catch (error) {
            console.log("error", error);
        }
    };

    useEffect(() => {
        fetchWarehouses();
        fetchSellerList();
    }, []);

    function sellerFilterChangeHandler(e: any) {
        const value = e.target.value;
        setSellerFilterId(value);
        fetchWarehouses(value);
    }

    async function viewWarehouse(id: any) {
        setViewModalOpen(true);
        setViewLoading(true);
        setViewData(null);
        try {
            const { data } = await axios({
                url: `${apiEndpoint}/admin/warehouses/${id}`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`,
                },
            });
            setViewData(data?.data || data);
        } catch (error) {
            console.log("error", error);
            toast.error("Error loading warehouse details");
        } finally {
            setViewLoading(false);
        }
    }

    function closeViewModal() {
        setViewModalOpen(false);
        setViewData(null);
    }

    function formatLabel(key: string) {
        return key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    const COLUMNS = [
        {
            Header: "Sn No.",
            Footer: "Sn No.",
            accessor: (row: any, count: any) => <>{count + 1}</>,
            disableFilters: true,
            sticky: "left",
        },
        {
            Header: "Warehouse ID",
            Footer: "Warehouse ID",
            accessor: (row: any) => row?.id ?? "-",
            sticky: "left",
        },
        {
            Header: "Warehouse Name",
            Footer: "Warehouse Name",
            accessor: (row: any) => (
                <div>
                    <span className={warehouseStyles.warehouseName}>
                        {row?.warehouse_name || "Default Store"}
                    </span>
                    {row?.warehouse_code ? (
                        <div className={warehouseStyles.warehouseCode}>{row.warehouse_code}</div>
                    ) : null}
                </div>
            ),
            sticky: "left",
        },
        {
            Header: "Seller",
            Footer: "Seller",
            accessor: (row: any) => row?.seller_name || `#${row?.seller_id}` || "-",
            sticky: "left",
        },
        {
            Header: "Location",
            Footer: "Location",
            accessor: (row: any) => {
                const parts = [row?.city, row?.state].filter(Boolean);
                return parts.length ? parts.join(", ") : "-";
            },
            sticky: "left",
        },
        {
            Header: "Contact",
            Footer: "Contact",
            accessor: (row: any) => (
                <div>
                    <div>{row?.warehouse_contact_person || "-"}</div>
                    {row?.email ? <div className={warehouseStyles.warehouseCode}>{row.email}</div> : null}
                </div>
            ),
            sticky: "left",
        },
        {
            Header: "Status",
            Footer: "Status",
            accessor: (row: any) => (
                <span className={row?.status ? brandStyles.statusBadgeActive : brandStyles.statusBadgeInactive}>
                    {row?.status ? "Active" : "Inactive"}
                </span>
            ),
            sticky: "left",
        },
        {
            Header: "Action",
            Footer: "Action",
            accessor: (row: any) => (
                <span className={brandStyles.iconBtn} onClick={() => viewWarehouse(row?.id)}>
                    <VisibilityOutlinedIcon style={{ color: "#4c2fb3" }} titleAccess="View" />
                </span>
            ),
            sticky: "left",
        },
    ];

    return (
        <ThemeMaker>
            <div className={`${warehouseStyles.pageWrap} mcrb-page`}>
                <div className={`${brandStyles.cardOuter} mcrb-card`}>
                    <div className={brandStyles.headerBar}>
                        <span className={brandStyles.title}>Warehouse List</span>
                    </div>

                    <div className="mcrb-toolbar">
                        <label htmlFor="wh-seller-filter">Seller</label>
                        <select
                            id="wh-seller-filter"
                            value={sellerFilterId}
                            onChange={sellerFilterChangeHandler}
                        >
                            <option value="">All Sellers</option>
                            {sellerList.map((item: any) => (
                                <option value={item?.seller_id} key={item?.seller_id}>{item?.company_name} ({item?.contact_no})</option>
                            ))}
                        </select>
                    </div>

                    <div className={`${brandStyles.bodyArea} ${brandStyles.tableWrap}`} style={{ minHeight: "60vh" }}>
                        {isLoading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
                                <CircularProgress />
                            </div>
                        ) : (
                            <Paginated data={warehouseList} columns={COLUMNS} />
                        )}
                    </div>
                </div>
            </div>

            <Modal
                open={viewModalOpen}
                onClose={closeViewModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={Sstyle} className={`col-md-4 w-100 w-md-25 ${warehouseStyles.modalBox}`}>
                    <div className={warehouseStyles.modalHeader}>
                        <p className={warehouseStyles.modalTitle}>Warehouse Details</p>
                        <span className={warehouseStyles.modalClose}><CloseIcon onClick={closeViewModal} /></span>
                    </div>
                    <div className={warehouseStyles.modalBody}>
                        {viewLoading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "160px" }}>
                                <CircularProgress size={28} />
                            </div>
                        ) : viewData ? (
                            <div className={warehouseStyles.detailGrid}>
                                {Object.entries(viewData)
                                    .filter(([key, value]) => typeof value !== "object" || value === null)
                                    .map(([key, value]: any) => (
                                        <div key={key} className={warehouseStyles.detailRow}>
                                            <div className={warehouseStyles.detailLabel}>{formatLabel(key)}</div>
                                            <div className={warehouseStyles.detailValue}>{value === null || value === "" ? "-" : String(value)}</div>
                                        </div>
                                    ))}
                            </div>
                        ) : (
                            <p className="text-center">No details found.</p>
                        )}
                    </div>
                </Box>
            </Modal>
        </ThemeMaker>
    );
};

export default WarehouseList;
